import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

// ⚠️ Backend qui sert /uploads (ton cas: http://localhost:3000)
const BACK_URL = "https://backend.fmcadventistyouth.com";


/** Décodage base64 URL-safe (gère -/_ et le padding manquant). */
function b64UrlDecode(input) {
  try {
    let s = String(input || "").replace(/-/g, "+").replace(/_/g, "/");
    s += "===".slice((s.length + 3) % 4);
    const bin = atob(s);
    try {
      return decodeURIComponent(
        Array.prototype
          .map.call(bin, (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
    } catch {
      return bin; // ASCII
    }
  } catch {
    return "";
  }
}

// Petit helper pour HEAD
async function head(url) {
  try {
    const res = await fetch(url, { method: "HEAD" });
    return { ok: res.ok, status: res.status, statusText: res.statusText };
  } catch (e) {
    return { ok: false, status: -1, statusText: String(e?.message || e) };
  }
}

export default function DocumentViewerEglise() {
  const [imgError, setImgError] = useState(false);
  const [netInfo, setNetInfo] = useState(null); // {ok,status,statusText}
  const [params] = useSearchParams();
  const b64 = params.get("path") || "";

  // 1) Valeur décodée depuis ?path=
  const decoded = useMemo(() => b64UrlDecode(b64), [b64]);

  // 2) Si c'est déjà une URL absolue, on l'utilise telle quelle (ex: http://localhost:3000/uploads/…)
  const isAbsolute = /^https?:\/\//i.test(decoded);

  // 3) Sinon on fabrique l'URL complète vers le back
  const rel = useMemo(() => {
    if (isAbsolute) return "";
    // on enlève les / initiaux et un éventuel "uploads/"
    return decoded.replace(/^\/+/, "").replace(/^uploads\/?/i, "");
  }, [decoded, isAbsolute]);

  const fileUrl = useMemo(() => {
    if (!decoded) return "";
    if (isAbsolute) return decoded;
    // construit l’URL absolue vers le backend qui sert /uploads
    return `${BACK_URL.replace(/\/$/, "")}/uploads/${rel}`;
  }, [decoded, isAbsolute, rel]);

  const isImage = useMemo(() => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(decoded), [decoded]);
  const isPdf   = useMemo(() => /\.pdf$/i.test(decoded), [decoded]);

  // 🔎 LOGS de débogage (toujours visibles dans la console)
  useEffect(() => {
    console.group("[DocumentViewer]");
    console.log("BACK_URL       :", BACK_URL);
    console.log("b64 (raw)      :", b64);
    console.log("decoded        :", decoded);
    console.log("isAbsolute     :", isAbsolute);
    console.log("rel            :", rel);
    console.log("fileUrl (final):", fileUrl);
    console.log("isImage / isPdf:", isImage, isPdf);
    console.groupEnd();
  }, [b64, decoded, isAbsolute, rel, fileUrl, isImage, isPdf]);

  // 🔎 Test réseau (HEAD) pour voir le statut HTTP exact
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!fileUrl) {
        setNetInfo(null);
        return;
      }
      const info = await head(fileUrl);
      if (!cancelled) {
        setNetInfo(info);
        console.group("[DocumentViewer][HEAD]");
        console.log("URL   :", fileUrl);
        console.log("ok    :", info.ok);
        console.log("status:", info.status, info.statusText || "");
        console.groupEnd();
      }
    })();
    return () => { cancelled = true; };
  }, [fileUrl]);

  return (
    <div style={{ minHeight: "100vh", background: "#0b0b0b", color: "#fff" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", borderBottom: "1px solid #222"
      }}>
        <div style={{ fontWeight: 600 }}>Visionneuse de document</div>
        {fileUrl && (
          <div style={{ display: "flex", gap: 8 }}>
            <a href={fileUrl} download className="btn btn-sm btn-light">Télécharger</a>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-light">
              Ouvrir l’original
            </a>
          </div>
        )}
      </div>

      <div style={{ padding: 16, display: "flex", justifyContent: "center" }}>
        {!decoded ? (
          <div style={{ color: "#ff6b6b" }}>
            Paramètre <code>path</code> manquant ou illisible.
          </div>
        ) : !fileUrl ? (
          <div style={{ color: "#ff6b6b" }}>
            Impossible de construire l’URL du fichier.
            <div style={{ marginTop: 8, fontSize: 12, color: "#ddd" }}>
              Décodé: <code>{decoded}</code>
            </div>
          </div>
        ) : isImage ? (
          imgError ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#ff6b6b", marginBottom: 8 }}>
                Impossible d’afficher l’image.
              </div>
              <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-light">
                Ouvrir l’original
              </a>
              <div style={{ marginTop: 10, fontSize: 12, color: "#ccc" }}>
                URL testée : <code>{fileUrl}</code>
              </div>
              {netInfo && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#ccc" }}>
                  HEAD: {String(netInfo.status)} {netInfo.statusText || ""} (ok: {String(netInfo.ok)})
                </div>
              )}
            </div>
          ) : (
            <img
              src={fileUrl}
              alt="aperçu"
              onError={() => {
                console.error("[DocumentViewer] onError IMG pour:", fileUrl);
                setImgError(true);
              }}
              onLoad={() => console.log("[DocumentViewer] image chargée:", fileUrl)}
              style={{ maxWidth: "100%", maxHeight: "calc(100vh - 80px)", borderRadius: 8, boxShadow: "0 0 0 1px #222" }}
            />
          )
        ) : isPdf ? (
          <object
            data={fileUrl}
            type="application/pdf"
            style={{ width: "100%", height: "calc(100vh - 64px)", border: "none", background: "#1a1a1a" }}
            onLoad={() => console.log("[DocumentViewer] PDF tenté via <object>:", fileUrl)}
          >
            <div className="text-center">
              <p>PDF non intégrable ici.</p>
              <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-light">
                Ouvrir le PDF
              </a>
              <div style={{ marginTop: 10, fontSize: 12, color: "#ccc" }}>
                URL testée : <code>{fileUrl}</code>
              </div>
              {netInfo && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#ccc" }}>
                  HEAD: {String(netInfo.status)} {netInfo.statusText || ""} (ok: {String(netInfo.ok)})
                </div>
              )}
            </div>
          </object>
        ) : (
          <div className="text-center">
            <p>Type non prévisualisable.</p>
            <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-light">
              Télécharger / Ouvrir
            </a>
            <div style={{ marginTop: 10, fontSize: 12, color: "#ccc" }}>
              URL testée : <code>{fileUrl}</code>
            </div>
            {netInfo && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#ccc" }}>
                HEAD: {String(netInfo.status)} {netInfo.statusText || ""} (ok: {String(netInfo.ok)})
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
